import { supabase } from "../supabase/auth";
import { logActivity } from "../activityLog";

/**
 * `ingredients.protein_class` — nullable text with a CHECK constraint.
 * NULL means "not yet classified". Never write an empty string here.
 */
export const PROTEIN_CLASSES = [
  "hydrolysed_protein",
  "isolated_protein",
  "protein_concentrate",
  "pulse_flour",
  "other_flour",
  "not_protein",
] as const;

export type ProteinClass = typeof PROTEIN_CLASSES[number];

/** Short display labels. `t(key, { defaultValue: label[lang] })` at the call site. */
export const PROTEIN_CLASS_LABELS: Record<ProteinClass, { key: string; en: string; ko: string }> = {
  hydrolysed_protein: { key: "proteinClass.hydrolysed_protein", en: "Hydrolysed", ko: "가수분해단백" },
  isolated_protein: { key: "proteinClass.isolated_protein", en: "Isolate", ko: "분리단백" },
  protein_concentrate: { key: "proteinClass.protein_concentrate", en: "Concentrate", ko: "농축단백" },
  pulse_flour: { key: "proteinClass.pulse_flour", en: "Pulse flour", ko: "두류 가루" },
  other_flour: { key: "proteinClass.other_flour", en: "Other flour", ko: "기타 가루" },
  not_protein: { key: "proteinClass.not_protein", en: "Not protein", ko: "비단백" },
};

/** Blank string from a <select> means unset, which is NULL in the database. */
export function toProteinClass(raw: string): ProteinClass | null {
  const v = raw.trim();
  if (!v) return null;
  return (PROTEIN_CLASSES as readonly string[]).includes(v) ? (v as ProteinClass) : null;
}

export interface Ingredient {
  id: string;
  common_name: string;
  korean_name: string | null;
  fda_name: string | null;
  supplier: string | null;
  color: string | null;
  tags: string[];
  is_active: boolean;
  protein_per_100g: number | null;
  /** NULL = not yet classified. Must stay nullable in every write path. */
  protein_class: ProteinClass | null;
  digestibility: number | null;
  fat: number | null;
  satfat: number | null;
  transfat: number | null;
  cholesterol: number | null;
  carbs: number | null;
  fiber: number | null;
  sugar: number | null;
  sodium: number | null;
  moisture: number | null;
  ash: number | null;
  cost_per_kg: number | null;
  aa_his: number | null;
  aa_ile: number | null;
  aa_leu: number | null;
  aa_lys: number | null;
  aa_mc: number | null;
  aa_pt: number | null;
  aa_thr: number | null;
  aa_trp: number | null;
  aa_val: number | null;
  vitamin_d: number | null;
  calcium: number | null;
  iron: number | null;
  potassium: number | null;
  sub_ingredients: string | null;
  notes_spec_ref: string | null;
  // Fatty acids (g/100g)
  fa_ala: number | null;
  fa_la: number | null;
  fa_oleic: number | null;
  fa_palmitic: number | null;
  fa_stearic: number | null;
  // Fiber fractions (g/100g)
  fiber_insoluble: number | null;
  fiber_soluble: number | null;
  resistant_starch: number | null;
  // BCAA (mg/g protein, derived: aa_leu + aa_ile + aa_val)
  bcaa_mg_per_g: number | null;
  // Reference digestibility
  ref_digest_product: string | null;
  ref_digest_value: number | null;
  ref_digest_source: string | null;
  // Per-field data sources
  data_sources: Record<string, string> | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by_email: string | null;
  archived_at: string | null;
  archived_by_email: string | null;
}

export interface SaveResult {
  success: boolean;
  data?: Ingredient;
  errors: string[];
  warnings: string[];
}

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;

/** String form for diffing and change-log values. Plain objects (e.g.
 *  data_sources) must be JSON-stringified — String(obj) gives "[object Object]"
 *  on both sides, so edits would never be detected and would be silently dropped. */
function toComparableString(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function rangeCheck(val: number | null | undefined, min: number, max: number, label: string, errors: string[]) {
  if (val == null) return;
  if (val < min || val > max) errors.push(`${label} must be between ${min} and ${max}`);
}

function minCheck(val: number | null | undefined, min: number, label: string, errors: string[]) {
  if (val == null) return;
  if (val < min) errors.push(`${label} must be >= ${min}`);
}

function validate(input: Partial<Ingredient>, mode: "create" | "edit"): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (mode === "create" && (!input.id || !input.id.trim())) {
    errors.push("ID is required");
  }
  if (!input.common_name || !input.common_name.trim()) {
    errors.push("Common Name is required");
  } else if (input.common_name.trim().length > 255) {
    errors.push("Common Name must be at most 255 characters");
  }

  // digestibility is NOT NULL in the database with no default, so a blank field
  // reaches Postgres as null and comes back as an opaque 400. Catch it here with
  // a message that names the field. No default is supplied: every one of the 219
  // active ingredients carries a real value, and a silent 0 would zero PDCAAS.
  if (mode === "create" && input.digestibility == null) {
    errors.push("Protein digestibility is required and must be between 0 and 1");
  }
  if (input.digestibility != null && (input.digestibility < 0 || input.digestibility > 1)) {
    errors.push("Protein digestibility must be between 0 and 1");
  }

  // protein_class is nullable. Only a non-empty value that is not in the allowed
  // set is an error — otherwise the database CHECK constraint returns a bare 400.
  if (input.protein_class != null && input.protein_class !== ("" as ProteinClass)
    && !(PROTEIN_CLASSES as readonly string[]).includes(input.protein_class)) {
    errors.push("Protein class is not one of the allowed values");
  }
  rangeCheck(input.protein_per_100g, 0, 100, "Protein per 100g", errors);
  rangeCheck(input.fat, 0, 100, "Fat", errors);
  rangeCheck(input.satfat, 0, 100, "Saturated Fat", errors);
  rangeCheck(input.transfat, 0, 100, "Trans Fat", errors);
  rangeCheck(input.carbs, 0, 100, "Carbs", errors);
  rangeCheck(input.fiber, 0, 100, "Fiber", errors);
  rangeCheck(input.sugar, 0, 100, "Sugar", errors);
  rangeCheck(input.moisture, 0, 100, "Moisture", errors);
  rangeCheck(input.ash, 0, 100, "Ash", errors);
  minCheck(input.cost_per_kg, 0, "Cost/kg", errors);
  minCheck(input.cholesterol, 0, "Cholesterol", errors);
  minCheck(input.sodium, 0, "Sodium", errors);
  minCheck(input.vitamin_d, 0, "Vitamin D", errors);
  minCheck(input.calcium, 0, "Calcium", errors);
  minCheck(input.iron, 0, "Iron", errors);
  minCheck(input.potassium, 0, "Potassium", errors);

  // Fatty acids
  minCheck(input.fa_ala, 0, "ALA (fa_ala)", errors);
  minCheck(input.fa_la, 0, "LA (fa_la)", errors);
  minCheck(input.fa_oleic, 0, "Oleic (fa_oleic)", errors);
  minCheck(input.fa_palmitic, 0, "Palmitic (fa_palmitic)", errors);
  minCheck(input.fa_stearic, 0, "Stearic (fa_stearic)", errors);

  // Fiber fractions
  rangeCheck(input.fiber_insoluble, 0, 100, "Insoluble Fiber", errors);
  rangeCheck(input.fiber_soluble, 0, 100, "Soluble Fiber", errors);
  minCheck(input.resistant_starch, 0, "Resistant Starch", errors);

  // Reference digestibility
  if (input.ref_digest_value != null && (input.ref_digest_value < 0 || input.ref_digest_value > 1)) {
    errors.push("Reference digestibility value must be between 0 and 1");
  }

  const aaFields: (keyof Ingredient)[] = ["aa_his", "aa_ile", "aa_leu", "aa_lys", "aa_mc", "aa_pt", "aa_thr", "aa_trp", "aa_val"];
  for (const f of aaFields) {
    const v = input[f] as number | null | undefined;
    minCheck(v, 0, f, errors);
    if (v != null && v > 300) warnings.push(`${f} > 300 — verify this is correct`);
  }

  if (input.color && !COLOR_RE.test(input.color)) {
    errors.push("Color must be in #RRGGBB format");
  }

  if (input.tags) {
    const seen = new Set<string>();
    for (const t of input.tags) {
      const trimmed = t.trim();
      if (!trimmed) { errors.push("Tags must not be empty strings"); break; }
      if (trimmed.length > 50) { errors.push(`Tag "${trimmed}" exceeds 50 characters`); break; }
      if (seen.has(trimmed.toLowerCase())) { errors.push(`Duplicate tag: "${trimmed}"`); break; }
      seen.add(trimmed.toLowerCase());
    }
  }

  // Soft warnings
  if (input.satfat != null && input.fat != null && input.satfat > input.fat) {
    warnings.push("Saturated fat exceeds total fat");
  }
  if (input.fiber != null && input.carbs != null && input.fiber > input.carbs) {
    warnings.push("Fiber exceeds total carbs");
  }
  if (input.sugar != null && input.carbs != null && input.sugar > input.carbs) {
    warnings.push("Sugar exceeds total carbs");
  }
  if (input.fiber_soluble != null && input.fiber_insoluble != null && input.fiber != null &&
    input.fiber_soluble + input.fiber_insoluble > input.fiber) {
    warnings.push("Soluble + insoluble fiber exceeds total fiber");
  }
  if (input.resistant_starch != null && input.fiber != null && input.resistant_starch > input.fiber) {
    warnings.push("Resistant starch exceeds total fiber");
  }

  return { errors, warnings };
}

export async function saveIngredient(
  input: Partial<Ingredient>,
  mode: "create" | "edit",
  originalRow: Ingredient | null,
  userEmail: string,
): Promise<SaveResult> {
  const { errors, warnings } = validate(input, mode);
  if (errors.length > 0) return { success: false, errors, warnings };

  // Only `id` is unique in the database, so two rows can silently share a
  // common_name and every downstream name-keyed lookup picks one at random.
  // Block a save that would create (or rename into) a duplicate; an edit that
  // keeps its existing name is untouched even if it is a legacy duplicate.
  const trimmedName = input.common_name!.trim();
  const nameChanged = mode === "create" || originalRow?.common_name?.trim() !== trimmedName;
  if (nameChanged) {
    // Escape LIKE wildcards — ingredient names legitimately contain "%" ("Fava 85%").
    const likeSafe = trimmedName.replace(/[\\%_]/g, (m) => `\\${m}`);
    const { data: sameName } = await supabase
      .from("ingredients")
      .select("id, common_name")
      .ilike("common_name", likeSafe)
      .limit(2);
    const clash = (sameName ?? []).find((r) => r.id !== input.id?.trim());
    if (clash) {
      return {
        success: false,
        errors: [`Another ingredient (${clash.id}) already uses the name "${trimmedName}"`],
        warnings,
      };
    }
  }

  const now = new Date().toISOString();

  if (mode === "create") {
    // Check id uniqueness
    const { data: existing } = await supabase
      .from("ingredients")
      .select("id")
      .eq("id", input.id!.trim())
      .maybeSingle();
    if (existing) return { success: false, errors: ["An ingredient with this ID already exists"], warnings };

    const row: Record<string, unknown> = {
      id: input.id!.trim(),
      common_name: input.common_name!.trim(),
      korean_name: input.korean_name?.trim() || null,
      fda_name: input.fda_name?.trim() || input.common_name!.trim(),
      supplier: input.supplier?.trim() || null,
      color: input.color?.trim() || "#888888",
      tags: (input.tags || []).map((t) => t.trim()).filter(Boolean),
      is_active: true,
      protein_per_100g: input.protein_per_100g ?? 0,
      // Nullable, no default. Unclassified saves as NULL, never "".
      protein_class: input.protein_class || null,
      digestibility: input.digestibility,
      fat: input.fat ?? 0,
      satfat: input.satfat ?? 0,
      transfat: input.transfat ?? 0,
      cholesterol: input.cholesterol ?? 0,
      carbs: input.carbs ?? 0,
      fiber: input.fiber ?? 0,
      sugar: input.sugar ?? 0,
      sodium: input.sodium ?? 0,
      moisture: input.moisture ?? 0,
      ash: input.ash ?? null,
      cost_per_kg: input.cost_per_kg ?? 0,
      aa_his: input.aa_his ?? 0,
      aa_ile: input.aa_ile ?? 0,
      aa_leu: input.aa_leu ?? 0,
      aa_lys: input.aa_lys ?? 0,
      aa_mc: input.aa_mc ?? 0,
      aa_pt: input.aa_pt ?? 0,
      aa_thr: input.aa_thr ?? 0,
      aa_trp: input.aa_trp ?? 0,
      aa_val: input.aa_val ?? 0,
      vitamin_d: input.vitamin_d ?? 0,
      calcium: input.calcium ?? 0,
      iron: input.iron ?? 0,
      potassium: input.potassium ?? 0,
      sub_ingredients: input.sub_ingredients?.trim() || null,
      notes_spec_ref: input.notes_spec_ref?.trim() || null,
      fa_ala: input.fa_ala ?? 0,
      fa_la: input.fa_la ?? 0,
      fa_oleic: input.fa_oleic ?? 0,
      fa_palmitic: input.fa_palmitic ?? 0,
      fa_stearic: input.fa_stearic ?? 0,
      fiber_insoluble: input.fiber_insoluble ?? 0,
      fiber_soluble: input.fiber_soluble ?? 0,
      resistant_starch: input.resistant_starch ?? 0,
      bcaa_mg_per_g: (input.aa_leu != null || input.aa_ile != null || input.aa_val != null)
        ? (input.aa_leu ?? 0) + (input.aa_ile ?? 0) + (input.aa_val ?? 0) : null,
      ref_digest_product: input.ref_digest_product?.trim() || null,
      ref_digest_value: input.ref_digest_value ?? null,
      ref_digest_source: input.ref_digest_source?.trim() || null,
      data_sources: input.data_sources || null,
      updated_at: now,
      updated_by_email: userEmail,
    };

    const { data: saved, error } = await supabase
      .from("ingredients")
      .insert(row)
      .select("*")
      .single();

    if (error || !saved) {
      return { success: false, errors: [error?.message || "Insert failed"], warnings };
    }

    // Write change log — one row per populated field
    const logRows: Record<string, unknown>[] = [];
    const skipFields = new Set(["created_at", "updated_at", "updated_by_email"]);
    for (const [col, val] of Object.entries(row)) {
      if (skipFields.has(col)) continue;
      if (val == null) continue;
      logRows.push({
        table_name: "ingredients",
        record_pk: saved.id,
        ingredient_id: saved.id,
        changed_by_email: userEmail,
        changed_column: col,
        old_value: null,
        new_value: toComparableString(val),
        changed_at: now,
      });
    }

    if (logRows.length > 0) {
      const { error: logError } = await supabase.from("data_change_log").insert(logRows);
      if (logError) {
        warnings.push("Ingredient saved, but change log write failed");
      }
    }

    logActivity("ingredient_create", {
      id: (saved as Ingredient).id,
      common_name: (saved as Ingredient).common_name,
    });

    return { success: true, data: saved as Ingredient, errors: [], warnings };
  }

  // ── EDIT MODE ──
  if (!originalRow) return { success: false, errors: ["Original row required for edit"], warnings };

  // Optimistic concurrency check
  const { data: currentRow } = await supabase
    .from("ingredients")
    .select("updated_at")
    .eq("id", originalRow.id)
    .single();

  if (currentRow) {
    const dbUpdatedAt = currentRow.updated_at;
    const origUpdatedAt = originalRow.updated_at;
    const bothNull = dbUpdatedAt == null && origUpdatedAt == null;
    if (!bothNull && dbUpdatedAt !== origUpdatedAt) {
      return { success: false, errors: ["Record updated elsewhere, reload before saving"], warnings };
    }
  }

  // Compute diff
  const changes: Record<string, { oldVal: unknown; newVal: unknown }> = {};
  const updatePayload: Record<string, unknown> = {
    updated_at: now,
    updated_by_email: userEmail,
  };

  const editableFields: (keyof Ingredient)[] = [
    "common_name", "korean_name", "fda_name", "supplier", "color", "tags", "is_active",
    "protein_per_100g", "protein_class", "digestibility", "fat", "satfat", "transfat", "cholesterol",
    "carbs", "fiber", "sugar", "sodium", "moisture", "cost_per_kg",
    "aa_his", "aa_ile", "aa_leu", "aa_lys", "aa_mc", "aa_pt", "aa_thr", "aa_trp", "aa_val",
    "vitamin_d", "calcium", "iron", "potassium", "sub_ingredients", "notes_spec_ref",
    "fa_ala", "fa_la", "fa_oleic", "fa_palmitic", "fa_stearic",
    "fiber_insoluble", "fiber_soluble", "resistant_starch", "bcaa_mg_per_g", "ash",
    "ref_digest_product", "ref_digest_value", "ref_digest_source", "data_sources",
  ];

  for (const field of editableFields) {
    let newVal = input[field];
    const oldVal = originalRow[field];

    if (typeof newVal === "string") newVal = newVal.trim();
    // Nullable text column: an empty selection is NULL, never "" (a "" would fail
    // the CHECK constraint and come back as a silent 400).
    if (field === "protein_class" && newVal === "") newVal = null;

    const oldStr = toComparableString(oldVal);
    const newStr = toComparableString(newVal);

    if (oldStr !== newStr) {
      changes[field] = { oldVal, newVal };
      updatePayload[field] = newVal;
    }
  }

  if (Object.keys(changes).length === 0) {
    return { success: true, data: originalRow, errors: [], warnings: ["No changes detected"] };
  }

  const { data: saved, error } = await supabase
    .from("ingredients")
    .update(updatePayload)
    .eq("id", originalRow.id)
    .select("*")
    .single();

  if (error || !saved) {
    return { success: false, errors: [error?.message || "Update failed"], warnings };
  }

  // Write change log
  console.log("Writing change log entries: " + Object.keys(changes).length);
  const logRows = Object.entries(changes).map(([col, { oldVal, newVal }]) => ({
    table_name: "ingredients",
    record_pk: originalRow.id,
    ingredient_id: originalRow.id,
    changed_by_email: userEmail,
    changed_column: col,
    old_value: oldVal == null ? null : toComparableString(oldVal),
    new_value: newVal == null ? null : toComparableString(newVal),
    changed_at: now,
  }));

  if (logRows.length > 0) {
    const { error: logError } = await supabase.from("data_change_log").insert(logRows);
    if (logError) {
      warnings.push("Ingredient saved, but change log write failed");
    }
  }

  logActivity("ingredient_update", {
    id: (saved as Ingredient).id,
    common_name: (saved as Ingredient).common_name,
    changed: logRows.map((r) => r.changed_column),
  });

  return { success: true, data: saved as Ingredient, errors: [], warnings };
}
